import React, { useState, useEffect, useMemo, useRef } from "react";
import {
    getUsers, getRoles, searchAzureUsers, createUser, updateUser,
    getSubcontractorPoolAccount, setSubcontractorPoolAccount, getSubcontractorPoolProjects,
    getUserSubcontractorProjects, setUserSubcontractorProjects,
    AppUser, Role, AzureUser, SubcontractorPoolProject,
} from "../services/api";

const primaryBtnStyle: React.CSSProperties = {
    backgroundColor: "#F18E00", color: "white", border: "none", borderRadius: "4px",
    padding: "9px 20px", cursor: "pointer", fontSize: "13px", fontWeight: "bold",
};
const secondaryBtnStyle: React.CSSProperties = {
    backgroundColor: "white", color: "#5C5D5E", border: "1px solid #ddd",
    borderRadius: "4px", padding: "9px 20px", cursor: "pointer", fontSize: "13px",
};
const inputStyle: React.CSSProperties = {
    width: "100%", padding: "8px 10px", fontSize: "13px",
    border: "1px solid #ddd", borderRadius: "4px", boxSizing: "border-box",
};
const thStyle: React.CSSProperties = {
    padding: "10px 12px", textAlign: "left", fontSize: "12px",
    textTransform: "uppercase", color: "white", backgroundColor: "#5C5D5E",
};
const tdStyle: React.CSSProperties = {
    padding: "10px 12px", fontSize: "13px", color: "#333", verticalAlign: "middle",
};
const sectionTitleStyle: React.CSSProperties = {
    color: "#5C5D5E", fontSize: "16px", fontWeight: "bold", margin: "0 0 12px 0",
};
const cardStyle: React.CSSProperties = {
    border: "1px solid #e0e0e0", borderRadius: "6px", padding: "20px",
    backgroundColor: "white", marginBottom: "28px",
};

// FastAPI's `detail` is usually a plain string (our own HTTPException calls),
// but on a validation error (422) it's a list of Pydantic error objects
// instead -- rendering that directly as a React child crashes the whole
// page (React error #31, "Objects are not valid as a React child"). This
// is exactly what happened when a route-ordering bug made a literal path
// get swallowed by a /users/{user_id} route and fail int-parsing: the raw
// {type, loc, msg, input} error object landed in component state and blew
// up the render. Route ordering is fixed, but every catch block here goes
// through this helper now so a similar backend response can never do that
// again -- worst case is a generic fallback message instead of a crash.
const extractErrorMessage = (e: any, fallback: string): string => {
    const detail = e?.response?.data?.detail;
    if (typeof detail === "string" && detail.trim()) return detail;
    return fallback;
};

const BADGE_COLORS = ["#5C5D5E", "#F18E00", "#2E7D32", "#1565C0", "#6A1B9A", "#AD1457", "#00838F"];

const badgeColorFor = (name: string): string => {
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
    return BADGE_COLORS[hash % BADGE_COLORS.length];
};

const RoleBadge: React.FC<{ name: string }> = ({ name }) => (
    <span style={{
        display: "inline-block", backgroundColor: badgeColorFor(name), color: "white",
        borderRadius: "10px", padding: "2px 10px", fontSize: "11px", fontWeight: "bold",
        marginRight: "4px", marginBottom: "4px",
    }}>
        {name}
    </span>
);

const emptyNewUserForm = {
    azure_ad_id: "",
    email: "",
    first_name: "",
    last_name: "",
    display_name: "",
};

const UserManagement: React.FC = () => {
    const [users, setUsers] = useState<AppUser[]>([]);
    const [roles, setRoles] = useState<Role[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);

    // --- Inline edit state ---
    const [editingUserId, setEditingUserId] = useState<number | null>(null);
    const [editRoles, setEditRoles] = useState<Set<string>>(new Set());
    const [editZohoId, setEditZohoId] = useState("");
    const [editIsActive, setEditIsActive] = useState(true);
    const [editCanApprove, setEditCanApprove] = useState(false);
    const [editIsSubcontractor, setEditIsSubcontractor] = useState(false);
    const [editSelectedProjects, setEditSelectedProjects] = useState<Set<number>>(new Set());
    const [editProjectsLoading, setEditProjectsLoading] = useState(false);
    const [savingEdit, setSavingEdit] = useState(false);
    const [editError, setEditError] = useState<string | null>(null);

    // --- Subcontractor pool account + project pool (shared by Add + Edit) ---
    const [poolZohoId, setPoolZohoId] = useState("");
    const [poolZohoIdInput, setPoolZohoIdInput] = useState("");
    const [poolProjects, setPoolProjects] = useState<SubcontractorPoolProject[]>([]);
    const [savingPoolAccount, setSavingPoolAccount] = useState(false);
    const [poolAccountMessage, setPoolAccountMessage] = useState<{ text: string; isError: boolean } | null>(null);

    // --- Quick status-toggle state (per row, separate from the edit panel) ---
    const [togglingUserId, setTogglingUserId] = useState<number | null>(null);

    // --- Add New User state ---
    const [search, setSearch] = useState("");
    const [searchResults, setSearchResults] = useState<AzureUser[]>([]);
    const [searching, setSearching] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const [newUserForm, setNewUserForm] = useState(emptyNewUserForm);
    const [newRoles, setNewRoles] = useState<Set<string>>(new Set());
    const [newZohoId, setNewZohoId] = useState("");
    const [newIsSubcontractor, setNewIsSubcontractor] = useState(false);
    const [creating, setCreating] = useState(false);
    const [createMessage, setCreateMessage] = useState<{ text: string; isError: boolean } | null>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const loadAll = async () => {
        setLoading(true);
        setLoadError(null);
        try {
            const [u, r] = await Promise.all([getUsers(), getRoles()]);
            setUsers(u);
            setRoles(r);

            // Subcontractor pool data is secondary to the core user list --
            // fetched separately so a hiccup here doesn't block the whole
            // page from loading.
            try {
                const [pool, poolAcct] = await Promise.all([
                    getSubcontractorPoolProjects(), getSubcontractorPoolAccount(),
                ]);
                setPoolProjects(pool);
                setPoolZohoId(poolAcct.zoho_user_id || "");
                setPoolZohoIdInput(poolAcct.zoho_user_id || "");
            } catch (poolErr) {
                console.error("Failed to load subcontractor pool data", poolErr);
            }
        } catch (e: any) {
            console.error("Failed to load users", e);
            const status = e?.response?.status;
            const detail = e?.response?.data?.detail;
            let message: string;
            if (typeof detail === "string") {
                message = detail;
            } else if (status === 403) {
                message = "You don't have permission to view User Management (admin role required).";
            } else if (status === 401) {
                message = "Your session has expired. Please refresh the page and sign in again.";
            } else {
                message = "Failed to load users. Please try again.";
            }
            setLoadError(message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadAll();
    }, []);

    // --- Debounced Azure AD search ---
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);

        const term = search.trim();
        if (term.length < 3) {
            setSearchResults([]);
            setShowResults(false);
            return;
        }

        debounceRef.current = setTimeout(async () => {
            setSearching(true);
            try {
                const results = await searchAzureUsers(term);
                setSearchResults(results);
                setShowResults(true);
            } catch (e) {
                console.error("Azure AD search failed", e);
                setSearchResults([]);
            } finally {
                setSearching(false);
            }
        }, 500);

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [search]);

    const startEdit = (user: AppUser) => {
        setEditingUserId(user.id);
        setEditRoles(new Set(user.roles));
        setEditZohoId(user.zoho_user_id || "");
        setEditIsActive(user.is_active);
        setEditCanApprove(user.can_approve_expenses);
        setEditIsSubcontractor(user.is_subcontractor);
        setEditSelectedProjects(new Set());
        setEditError(null);

        if (user.is_subcontractor) {
            setEditProjectsLoading(true);
            getUserSubcontractorProjects(user.id)
                .then(data => setEditSelectedProjects(new Set(data.project_ids)))
                .catch(e => console.error("Failed to load subcontractor project access", e))
                .finally(() => setEditProjectsLoading(false));
        }
    };

    const cancelEdit = () => {
        setEditingUserId(null);
        setEditError(null);
    };

    const toggleEditSubcontractor = (checked: boolean) => {
        setEditIsSubcontractor(checked);
        if (checked && editingUserId !== null && editSelectedProjects.size === 0) {
            setEditProjectsLoading(true);
            getUserSubcontractorProjects(editingUserId)
                .then(data => setEditSelectedProjects(new Set(data.project_ids)))
                .catch(e => console.error("Failed to load subcontractor project access", e))
                .finally(() => setEditProjectsLoading(false));
        }
    };

    const toggleEditProject = (projectId: number) => {
        setEditSelectedProjects(prev => {
            const next = new Set(prev);
            if (next.has(projectId)) next.delete(projectId); else next.add(projectId);
            return next;
        });
    };

    const toggleEditRole = (roleName: string) => {
        setEditRoles(prev => {
            const next = new Set(prev);
            if (next.has(roleName)) next.delete(roleName); else next.add(roleName);
            return next;
        });
    };

    const saveEdit = async (userId: number) => {
        setSavingEdit(true);
        setEditError(null);
        try {
            const updated = await updateUser(userId, {
                roles: Array.from(editRoles),
                zoho_user_id: editZohoId.trim() || null,
                is_active: editIsActive,
                can_approve_expenses: editCanApprove,
                is_subcontractor: editIsSubcontractor,
            });
            if (editIsSubcontractor) {
                await setUserSubcontractorProjects(userId, Array.from(editSelectedProjects));
            }
            setUsers(prev => prev.map(u => (u.id === userId ? updated : u)));
            setEditingUserId(null);
        } catch (e: any) {
            console.error("Failed to update user", e);
            setEditError(extractErrorMessage(e, "Failed to save changes. Please try again."));
        } finally {
            setSavingEdit(false);
        }
    };

    const savePoolAccount = async () => {
        const trimmed = poolZohoIdInput.trim();
        if (!trimmed) {
            setPoolAccountMessage({ text: "Enter the Zoho user ID for Amplify Subcontractor first.", isError: true });
            return;
        }
        setSavingPoolAccount(true);
        setPoolAccountMessage(null);
        try {
            await setSubcontractorPoolAccount(trimmed);
            setPoolZohoId(trimmed);
            const projects = await getSubcontractorPoolProjects();
            setPoolProjects(projects);
            setPoolAccountMessage({ text: "Saved. Run a Zoho assignments sync to pull in its projects.", isError: false });
        } catch (e: any) {
            console.error("Failed to save subcontractor pool account", e);
            setPoolAccountMessage({
                text: extractErrorMessage(e, "Failed to save. Please try again."),
                isError: true,
            });
        } finally {
            setSavingPoolAccount(false);
        }
    };

    const toggleActiveQuick = async (user: AppUser) => {
        setTogglingUserId(user.id);
        try {
            const updated = await updateUser(user.id, { is_active: !user.is_active });
            setUsers(prev => prev.map(u => (u.id === user.id ? updated : u)));
        } catch (e) {
            console.error("Failed to toggle status", e);
        } finally {
            setTogglingUserId(null);
        }
    };

    const selectAzureResult = (result: AzureUser) => {
        setNewUserForm({
            azure_ad_id: result.azure_ad_id,
            email: result.email,
            first_name: result.first_name,
            last_name: result.last_name,
            display_name: result.display_name,
        });
        setSearch(result.display_name);
        setShowResults(false);
        setCreateMessage(null);
    };

    const toggleNewRole = (roleName: string) => {
        setNewRoles(prev => {
            const next = new Set(prev);
            if (next.has(roleName)) next.delete(roleName); else next.add(roleName);
            return next;
        });
    };

    const resetAddUserForm = () => {
        setSearch("");
        setSearchResults([]);
        setShowResults(false);
        setNewUserForm(emptyNewUserForm);
        setNewRoles(new Set());
        setNewZohoId("");
        setNewIsSubcontractor(false);
    };

    const handleCreateUser = async () => {
        setCreateMessage(null);
        if (!newUserForm.azure_ad_id) {
            setCreateMessage({ text: "Search for and select an Azure AD user first.", isError: true });
            return;
        }
        setCreating(true);
        try {
            const created = await createUser({
                azure_ad_id:  newUserForm.azure_ad_id,
                email:        newUserForm.email,
                first_name:   newUserForm.first_name,
                last_name:    newUserForm.last_name,
                display_name: newUserForm.display_name,
                zoho_user_id: newZohoId.trim() || null,
                roles:        Array.from(newRoles),
                is_subcontractor: newIsSubcontractor,
            });
            setUsers(prev => [...prev, created].sort((a, b) => a.display_name.localeCompare(b.display_name)));
            resetAddUserForm();
            setCreateMessage({
                text: newIsSubcontractor
                    ? `${created.display_name} was added successfully. Use Edit below to grant them Project Access.`
                    : `${created.display_name} was added successfully.`,
                isError: false,
            });
        } catch (e: any) {
            console.error("Failed to create user", e);
            setCreateMessage({
                text: extractErrorMessage(e, "Failed to add user. Please try again."),
                isError: true,
            });
        } finally {
            setCreating(false);
        }
    };

    const sortedUsers = useMemo(
        () => [...users].sort((a, b) => a.display_name.localeCompare(b.display_name)),
        [users]
    );

    if (loading) return <div>Loading users...</div>;

    if (loadError) {
        return (
            <div>
                <div style={{ color: "#C00000", fontSize: "14px", marginBottom: "12px" }}>{loadError}</div>
                <button onClick={loadAll} style={secondaryBtnStyle}>Retry</button>
            </div>
        );
    }

    return (
        <div>
            <h1 style={{ color: "#5C5D5E", margin: "0 0 20px 0", fontSize: "22px" }}>User Management</h1>

            <div style={cardStyle}>
                <h2 style={sectionTitleStyle}>Current Users</h2>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                        <tr>
                            <th style={thStyle}>Name</th>
                            <th style={thStyle}>Email</th>
                            <th style={thStyle}>Roles</th>
                            <th style={thStyle}>Zoho User ID</th>
                            <th style={thStyle}>Status</th>
                            <th style={thStyle}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedUsers.map((user, idx) => (
                            <React.Fragment key={user.id}>
                                <tr style={{ backgroundColor: idx % 2 === 1 ? "#FDE8BC" : "white" }}>
                                    <td style={tdStyle}>
                                        {user.display_name}
                                        {user.is_subcontractor && (
                                            <span style={{
                                                display: "inline-block", marginLeft: "8px", backgroundColor: "#00838F",
                                                color: "white", borderRadius: "10px", padding: "2px 8px",
                                                fontSize: "10px", fontWeight: "bold",
                                            }}>
                                                SUBCONTRACTOR
                                            </span>
                                        )}
                                    </td>
                                    <td style={tdStyle}>{user.email}</td>
                                    <td style={tdStyle}>
                                        {user.roles.length === 0
                                            ? <span style={{ color: "#999", fontSize: "12px" }}>No roles</span>
                                            : user.roles.map(r => <RoleBadge key={r} name={r} />)}
                                    </td>
                                    <td style={tdStyle}>{user.zoho_user_id || <span style={{ color: "#999" }}>—</span>}</td>
                                    <td style={tdStyle}>
                                        <button
                                            onClick={() => toggleActiveQuick(user)}
                                            disabled={togglingUserId === user.id}
                                            style={{
                                                border: "none", borderRadius: "12px", padding: "4px 12px",
                                                fontSize: "11px", fontWeight: "bold", cursor: "pointer",
                                                color: "white",
                                                backgroundColor: user.is_active ? "#2E7D32" : "#999",
                                                opacity: togglingUserId === user.id ? 0.6 : 1,
                                            }}
                                        >
                                            {user.is_active ? "Active" : "Inactive"}
                                        </button>
                                    </td>
                                    <td style={tdStyle}>
                                        {editingUserId === user.id ? (
                                            <button onClick={cancelEdit} style={secondaryBtnStyle}>Close</button>
                                        ) : (
                                            <button onClick={() => startEdit(user)} style={secondaryBtnStyle}>Edit</button>
                                        )}
                                    </td>
                                </tr>
                                {editingUserId === user.id && (
                                    <tr>
                                        <td colSpan={6} style={{ padding: 0 }}>
                                            <div style={{
                                                backgroundColor: "#FFF8EC", border: "1px solid #F18E00",
                                                borderRadius: "6px", padding: "16px 20px", margin: "8px 0",
                                            }}>
                                                {editError && (
                                                    <div style={{ color: "#C00000", fontSize: "13px", marginBottom: "10px" }}>{editError}</div>
                                                )}
                                                <div style={{ display: "flex", gap: "32px", flexWrap: "wrap", marginBottom: "16px" }}>
                                                    <div>
                                                        <div style={{ fontSize: "11px", fontWeight: "bold", color: "#5C5D5E", textTransform: "uppercase", marginBottom: "6px" }}>
                                                            Roles
                                                        </div>
                                                        {roles.map(r => (
                                                            <label key={r.id} style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", color: "#333", marginBottom: "4px" }}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={editRoles.has(r.name)}
                                                                    onChange={() => toggleEditRole(r.name)}
                                                                />
                                                                {r.name}
                                                            </label>
                                                        ))}
                                                    </div>
                                                    <div style={{ minWidth: "200px" }}>
                                                        <div style={{ fontSize: "11px", fontWeight: "bold", color: "#5C5D5E", textTransform: "uppercase", marginBottom: "6px" }}>
                                                            Zoho User ID
                                                        </div>
                                                        <input
                                                            type="text"
                                                            value={editZohoId}
                                                            onChange={e => setEditZohoId(e.target.value)}
                                                            style={inputStyle}
                                                        />
                                                        <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", color: "#333", marginTop: "14px" }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={editIsActive}
                                                                onChange={e => setEditIsActive(e.target.checked)}
                                                            />
                                                            Active
                                                        </label>
                                                        <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", color: "#333", marginTop: "8px" }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={editCanApprove}
                                                                onChange={e => setEditCanApprove(e.target.checked)}
                                                            />
                                                            Can Approve Expenses
                                                        </label>
                                                        <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", color: "#333", marginTop: "8px" }}>
                                                            <input
                                                                type="checkbox"
                                                                checked={editIsSubcontractor}
                                                                onChange={e => toggleEditSubcontractor(e.target.checked)}
                                                            />
                                                            Subcontractor
                                                        </label>
                                                    </div>
                                                    {editIsSubcontractor && (
                                                        <div style={{ minWidth: "220px" }}>
                                                            <div style={{ fontSize: "11px", fontWeight: "bold", color: "#5C5D5E", textTransform: "uppercase", marginBottom: "6px" }}>
                                                                Project Access
                                                            </div>
                                                            {editProjectsLoading ? (
                                                                <div style={{ fontSize: "12px", color: "#999" }}>Loading…</div>
                                                            ) : poolProjects.length === 0 ? (
                                                                <div style={{ fontSize: "12px", color: "#999", maxWidth: "220px" }}>
                                                                    No projects are assigned to Amplify Subcontractor in Zoho yet.
                                                                </div>
                                                            ) : (
                                                                <div style={{ maxHeight: "160px", overflowY: "auto" }}>
                                                                    {poolProjects.map(p => (
                                                                        <label key={p.id} style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", color: "#333", marginBottom: "4px" }}>
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={editSelectedProjects.has(p.id)}
                                                                                onChange={() => toggleEditProject(p.id)}
                                                                            />
                                                                            {p.name}
                                                                        </label>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                                <div style={{ display: "flex", gap: "10px" }}>
                                                    <button
                                                        onClick={() => saveEdit(user.id)}
                                                        disabled={savingEdit}
                                                        style={{ ...primaryBtnStyle, opacity: savingEdit ? 0.6 : 1 }}
                                                    >
                                                        {savingEdit ? "Saving…" : "Save"}
                                                    </button>
                                                    <button onClick={cancelEdit} disabled={savingEdit} style={secondaryBtnStyle}>Cancel</button>
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>

            <div style={cardStyle}>
                <h2 style={sectionTitleStyle}>Amplify Subcontractor Pool Account</h2>
                <p style={{ fontSize: "13px", color: "#666", marginTop: 0, marginBottom: "14px", maxWidth: "640px" }}>
                    Every subcontractor is set up in Zoho under one shared generic user,
                    "Amplify Subcontractor". Enter its Zoho user ID here so the app can
                    pull the list of projects Zoho has granted it -- that list is the
                    pool an individual subcontractor's Project Access can be picked
                    from below. A project has to be assigned to Amplify Subcontractor
                    in Zoho first before it can be handed to a specific person here.
                </p>
                <div style={{ display: "flex", gap: "16px", alignItems: "flex-end", flexWrap: "wrap", marginBottom: "12px" }}>
                    <div style={{ minWidth: "260px" }}>
                        <div style={{ fontSize: "11px", fontWeight: "bold", color: "#5C5D5E", textTransform: "uppercase", marginBottom: "6px" }}>
                            Zoho User ID
                        </div>
                        <input
                            type="text"
                            value={poolZohoIdInput}
                            onChange={e => setPoolZohoIdInput(e.target.value)}
                            style={inputStyle}
                        />
                    </div>
                    <button
                        onClick={savePoolAccount}
                        disabled={savingPoolAccount}
                        style={{ ...primaryBtnStyle, opacity: savingPoolAccount ? 0.6 : 1 }}
                    >
                        {savingPoolAccount ? "Saving…" : "Save"}
                    </button>
                </div>
                {poolAccountMessage && (
                    <div style={{
                        color: poolAccountMessage.isError ? "#C00000" : "#2E7D32",
                        fontSize: "13px", marginBottom: "8px",
                    }}>
                        {poolAccountMessage.text}
                    </div>
                )}
                <div style={{ fontSize: "12px", color: "#666" }}>
                    {poolZohoId
                        ? `Currently linked to Zoho user ID ${poolZohoId}. `
                        : "Not configured yet. "}
                    {poolProjects.length > 0
                        ? `${poolProjects.length} project(s) currently granted: ${poolProjects.map(p => p.name).join(", ")}.`
                        : "No projects currently granted (run a Zoho assignments sync after saving)."}
                </div>
            </div>

            <div style={cardStyle}>
                <h2 style={sectionTitleStyle}>Add New User</h2>

                <div style={{ position: "relative", marginBottom: "16px", maxWidth: "420px" }}>
                    <input
                        type="text"
                        value={search}
                        onChange={e => {
                            setSearch(e.target.value);
                            setNewUserForm(emptyNewUserForm);
                        }}
                        placeholder="Search Azure AD by name or email..."
                        style={inputStyle}
                    />
                    {searching && (
                        <div style={{ fontSize: "12px", color: "#999", marginTop: "4px" }}>Searching…</div>
                    )}
                    {showResults && searchResults.length > 0 && (
                        <div style={{
                            position: "absolute", top: "100%", left: 0, right: 0, zIndex: 10,
                            backgroundColor: "white", border: "1px solid #ddd", borderRadius: "4px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.15)", maxHeight: "220px", overflowY: "auto",
                        }}>
                            {searchResults.map(r => (
                                <div
                                    key={r.azure_ad_id}
                                    onClick={() => selectAzureResult(r)}
                                    style={{ padding: "10px 12px", fontSize: "13px", cursor: "pointer", borderBottom: "1px solid #f0f0f0" }}
                                    onMouseDown={e => e.preventDefault()}
                                >
                                    <div style={{ fontWeight: "bold", color: "#5C5D5E" }}>{r.display_name}</div>
                                    <div style={{ fontSize: "11px", color: "#999" }}>{r.email}</div>
                                </div>
                            ))}
                        </div>
                    )}
                    {showResults && searchResults.length === 0 && !searching && (
                        <div style={{ fontSize: "12px", color: "#999", marginTop: "4px" }}>No matches found.</div>
                    )}
                </div>

                <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginBottom: "16px" }}>
                    <div style={{ flex: "1 1 180px" }}>
                        <div style={{ fontSize: "11px", fontWeight: "bold", color: "#5C5D5E", textTransform: "uppercase", marginBottom: "6px" }}>Display Name</div>
                        <input type="text" value={newUserForm.display_name} readOnly style={{ ...inputStyle, backgroundColor: "#f5f5f5" }} />
                    </div>
                    <div style={{ flex: "1 1 140px" }}>
                        <div style={{ fontSize: "11px", fontWeight: "bold", color: "#5C5D5E", textTransform: "uppercase", marginBottom: "6px" }}>First Name</div>
                        <input type="text" value={newUserForm.first_name} readOnly style={{ ...inputStyle, backgroundColor: "#f5f5f5" }} />
                    </div>
                    <div style={{ flex: "1 1 140px" }}>
                        <div style={{ fontSize: "11px", fontWeight: "bold", color: "#5C5D5E", textTransform: "uppercase", marginBottom: "6px" }}>Last Name</div>
                        <input type="text" value={newUserForm.last_name} readOnly style={{ ...inputStyle, backgroundColor: "#f5f5f5" }} />
                    </div>
                    <div style={{ flex: "1 1 220px" }}>
                        <div style={{ fontSize: "11px", fontWeight: "bold", color: "#5C5D5E", textTransform: "uppercase", marginBottom: "6px" }}>Email</div>
                        <input type="text" value={newUserForm.email} readOnly style={{ ...inputStyle, backgroundColor: "#f5f5f5" }} />
                    </div>
                </div>

                <div style={{ display: "flex", gap: "32px", flexWrap: "wrap", marginBottom: "18px" }}>
                    <div>
                        <div style={{ fontSize: "11px", fontWeight: "bold", color: "#5C5D5E", textTransform: "uppercase", marginBottom: "6px" }}>Roles</div>
                        {roles.map(r => (
                            <label key={r.id} style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", color: "#333", marginBottom: "4px" }}>
                                <input
                                    type="checkbox"
                                    checked={newRoles.has(r.name)}
                                    onChange={() => toggleNewRole(r.name)}
                                />
                                {r.name}
                            </label>
                        ))}
                    </div>
                    <div style={{ minWidth: "200px" }}>
                        <div style={{ fontSize: "11px", fontWeight: "bold", color: "#5C5D5E", textTransform: "uppercase", marginBottom: "6px" }}>
                            Zoho User ID <span style={{ textTransform: "none", fontWeight: "normal", color: "#999" }}>(optional)</span>
                        </div>
                        <input
                            type="text"
                            value={newZohoId}
                            onChange={e => setNewZohoId(e.target.value)}
                            style={inputStyle}
                        />
                        <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", color: "#333", marginTop: "14px" }}>
                            <input
                                type="checkbox"
                                checked={newIsSubcontractor}
                                onChange={e => setNewIsSubcontractor(e.target.checked)}
                            />
                            Subcontractor
                        </label>
                    </div>
                </div>

                {createMessage && (
                    <div style={{
                        color: createMessage.isError ? "#C00000" : "#2E7D32",
                        fontSize: "13px", marginBottom: "12px",
                    }}>
                        {createMessage.text}
                    </div>
                )}

                <button
                    onClick={handleCreateUser}
                    disabled={creating}
                    style={{ ...primaryBtnStyle, opacity: creating ? 0.6 : 1 }}
                >
                    {creating ? "Adding…" : "Add User"}
                </button>
            </div>
        </div>
    );
};

export default UserManagement;
